import React, { useEffect, useState } from 'react';
import Image from 'next/legacy/image';
import Link from 'next/link';
import Countdown from 'react-countdown';
import ReactMarkdown from 'react-markdown';
import { Tab, Tabs } from 'react-bootstrap';
import { useWeb3React } from '@web3-react/core';

import styles from '../styles/Shop.module.scss';
import TokenGate from './TokenGate';
import RaffleEntryForm from './RaffleEntryForm';
import ConnectButton from './ConnectButton';
import useRaffles from '../hooks/useRaffles';
import RaffleDetailsTab from './RaffleDetailsTab';
import RaffleParticipantsTab from './RaffleParticipantsTab';
import RaffleDrawPanel from './RaffleDrawPanel';
// import useRaffleDraw from '../hooks/useRaffleDraw';

const raffleAccount = process.env.NEXT_PUBLIC_RAFFLE_ACCOUNT;

const Raffle = ({ raffleKey }) => {
  const raffle = useRaffles(raffleKey);

  const { active } = useWeb3React();
  const [showCountdown, setShowCountdown] = useState(false);
  const [raffleStarted, setRaffleStarted] = useState(false);
  const [raffleEnded, setRaffleEnded] = useState(false);

  const [raffleStart, setRaffleStart] = useState(new Date(0));
  const [raffleEnd, setRaffleEnd] = useState(new Date());

  // const draw = useRaffleDraw(raffleKey);

  // console.log('draw', draw);

  useEffect(() => {
    if (raffle) {
      setRaffleStart(new Date(raffle.start_time));
      setRaffleEnd(new Date(raffle.end_time));
    }
  }, [raffle]);

  useEffect(() => {
    setShowCountdown(true); // only render countdown on client
    setRaffleStarted(raffleStart.getTime() < Date.now());
    setRaffleEnded(raffleEnd.getTime() < Date.now());
  }, [raffleStart, raffleEnd]);

  const raffleActive = raffleStarted && !raffleEnded;

  const socialLinks =
    raffle.twitter_url || raffle.discord_url || raffle.opensea_url;

  return (
    <>
      <div className="d-flex">
        <div>
          <Link href="/shop" className="btn btn-light rounded-pill px-4">
            <>
              <span className="me-1">&#10094;</span>Back
            </>
          </Link>
        </div>
        {showCountdown ? (
          <div className="ms-auto">
            <div className="btn btn-light rounded-pill px-4">
              {raffleActive ? (
                <>
                  Ends in{' '}
                  <strong>
                    <Countdown
                      date={raffleEnd}
                      days
                      onComplete={() => setRaffleEnded(true)}
                    />
                  </strong>
                </>
              ) : raffleEnded ? (
                <strong className="text-pink">Raffle ended</strong>
              ) : (
                <>
                  Starting in{' '}
                  <strong>
                    <Countdown
                      date={raffleStart}
                      days
                      onComplete={() => setRaffleStarted(true)}
                    />
                  </strong>
                </>
              )}
            </div>
          </div>
        ) : (
          ''
        )}
      </div>
      <article>
        <div className="row">
          <div className="col-12 col-lg-5 py-3">
            <div
              style={{
                borderRadius: '20px',
                overflow: 'hidden',
                display: 'flex',
              }}
            >
              <Image
                src={raffle.image}
                width={1200}
                height={1200}
                alt={raffle.title}
                unoptimized
              />
            </div>
          </div>
          <div className="col-12 col-lg-7 py-3">
            <div className={styles.card}>
              <div className="p-4 p-md-5">
                <h1 className="display-3 title">{raffle.title}</h1>
                {socialLinks && (
                  <div className="d-flex">
                    {raffle.twitter_url && (
                      <a
                        href={raffle.twitter_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pe-2"
                      >
                        <Image
                          src="/twitter-circle.svg"
                          alt="Twitter"
                          width={24}
                          height={24}
                        />
                      </a>
                    )}
                    {raffle.discord_url && (
                      <a
                        href={raffle.discord_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pe-2"
                      >
                        <Image
                          src="/discord-circle.svg"
                          alt="Discord"
                          width={24}
                          height={24}
                        />
                      </a>
                    )}
                    {raffle.opensea_url && (
                      <a
                        href={raffle.opensea_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pe-2"
                      >
                        <Image
                          src="/opensea-circle.svg"
                          alt="OpenSea"
                          width={24}
                          height={24}
                        />
                      </a>
                    )}
                  </div>
                )}
                {raffle.description ? (
                  <ReactMarkdown className="lead fw-normal">
                    {raffle.description}
                  </ReactMarkdown>
                ) : (
                  ''
                )}
                <Tabs
                  defaultActiveKey="details"
                  variant="pills"
                  className="pb-4 my-3 border-bottom"
                >
                  <Tab
                    eventKey="details"
                    title="Details"
                    tabClassName="rounded-pill px-4 me-3"
                  >
                    <RaffleDetailsTab raffleKey={raffleKey} />
                  </Tab>
                  <Tab
                    eventKey="participants"
                    title="Participants"
                    tabClassName="rounded-pill px-4 me-3"
                    disabled={
                      raffle.disable_ticket_sales || !raffle.automated_draw
                    }
                  >
                    <RaffleParticipantsTab raffleKey={raffleKey} />
                  </Tab>
                </Tabs>
              </div>
              <div className="p-4 p-md-5" style={{ backgroundColor: '#eee' }}>
                {raffleEnded ? (
                  <RaffleDrawPanel raffleKey={raffleKey} />
                ) : raffle.disable_ticket_sales ? (
                  <div>
                    <div className="alert alert-dark text-center lead">
                      You can&apos;t buy tickets for this raffle, see the rules
                      for more details.
                    </div>
                  </div>
                ) : (
                  <div>
                    {active ? (
                      <TokenGate onlyWrapped={raffle.wrapped}>
                        <RaffleEntryForm
                          raffleKey={raffleKey}
                          raffleAccount={raffleAccount}
                        />
                      </TokenGate>
                    ) : (
                      <div className="py-5 text-center">
                        <ConnectButton />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </article>
    </>
  );
};

export default Raffle;
